import { emptySenderBoxes, isSenderBox, type SignatureAnchor } from '@worki/shared';

/**
 * 1.1 — a box the sender has filled in lets go of its frame.
 *
 * The review: on the document to sign, the frame should go with a tap right
 * after the text is typed or the signature signed. A frame on the page marks a
 * place where somebody still has to act. Around the sender's own words or
 * drawing, once they are in, it marks nothing, and it is not on the page that
 * prints — so a box like that, once let go of, is only its words or its
 * drawing, as the stamp prints them. A tap on it brings the frame back, with
 * its bar, to change it or move it.
 *
 * Every other box keeps its frame: the recipients' places, and the sender's
 * own that are still empty, are where somebody still has to do something.
 *
 * Its own module, as the anchors' geometry is, so it is tested without a DOM.
 */

/**
 * Whether the sender has filled this box in: typed words into it, or put their
 * drawing on it. The other side of the empty boxes a send is refused for, so a
 * frame goes exactly when its box stops holding the send back.
 */
export function filledBySender(anchor: SignatureAnchor): boolean {
  return isSenderBox(anchor) && emptySenderBoxes([anchor]).length === 0;
}

/**
 * How a box is outlined on the screen that places it:
 * - `selected`: the box being worked on, with its bar under it;
 * - `marked`: a place somebody still has to act on;
 * - `none`: the sender's finished words or drawing, let go of.
 */
export type BoxOutline = 'selected' | 'marked' | 'none';

export function boxOutline(anchor: SignatureAnchor, selected: boolean): BoxOutline {
  if (selected) return 'selected';
  return filledBySender(anchor) ? 'none' : 'marked';
}

/**
 * What a tap on the page does while the box at `selected` is selected.
 *
 * A tap on the page puts a box down where it lands, as it always has — except
 * the tap that takes the frame off the sender's finished box, which only lets
 * go of it, the way a tap that closes a menu only closes it. That tap is aimed
 * at nothing in particular, and a box put down wherever it landed would be one
 * more box to delete.
 */
export function pageTap(anchors: SignatureAnchor[], selected: number | null): 'place' | 'let-go' {
  const anchor = selected === null ? undefined : anchors[selected];
  return anchor && filledBySender(anchor) ? 'let-go' : 'place';
}

/*
 * The frame and the tint for each outline. The edge itself (`border-2`) stays
 * on the box in every one, transparent when there is no outline, so the words
 * and the drawing do not move when the frame goes: they stay inside the inset
 * the signing page and the stamp set them in.
 */

/** Around the sender's words while they are typed into, on the screen that places them. */
export const INPUT_OUTLINE: Record<BoxOutline, string> = {
  selected: 'border-primary bg-primary/10',
  marked: 'border-dashed border-primary/70 bg-primary/10',
  none: 'border-transparent bg-transparent',
};

/** Around every other box on that screen: the recipients' places, and the sender's drawing. */
export const BOX_OUTLINE: Record<BoxOutline, string> = {
  selected: 'border-dashed border-primary bg-primary/25 text-primary cursor-move',
  marked: 'border-dashed border-primary/70 bg-primary/10 text-primary/90',
  none: 'border-transparent bg-transparent',
};
